import React, { useState } from 'react'
import styled from '@emotion/styled'
import ScrollableList from 'modules/ui/molecules/ScrollableList'
import useDoubleTheDonationCompaniesSearch from './use-double-the-donation-companies-search'
import { ListItem } from 'modules/ui/atoms/ListItem'
import Label from 'modules/ui/atoms/Typography/Label'
import { DEFAULT_FONT_STYLES, DEFAULT_THEME_COLORS } from 'css/theme'
import Caption from 'modules/ui/atoms/Typography/Caption'
import { ScrollableListData } from 'modules/ui/molecules/ScrollableList'
import { OrderDoubleTheDonationSliceValue } from 'modules/order/state/double-the-donation/order-double-the-donation-types'
import { useTranslation } from 'next-i18next'

export interface DoubleDonationProps {
  /** Selected Company name. */
  company?: string
  icon: string
  onChange?: () => void
  isToggleChecked?: boolean
  onSelectCompany: (value: OrderDoubleTheDonationSliceValue) => void
  onClearCompany: () => void
}

const DoubleDonationContainer = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;
`

const StyledButton = styled.div({
  color: 'rgba(46, 94, 217, 1)',
  display: 'flex',
  justifyContent: 'left',
  alignItems: 'left',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: DEFAULT_FONT_STYLES[`--theme-font--body-sm__font-size`],
  lineHeight: '21px',
  fontWeight: DEFAULT_FONT_STYLES['--theme-font--body-default__font-weight'],
  textDecoration: 'underline',
})

/**
 * Double Donation List option.
 */
const DoubleDonation: React.FC<DoubleDonationProps> = (props: DoubleDonationProps) => {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)

  const {
    onChange,
    isToggleChecked = false,
    icon,
    company,
    onSelectCompany,
    onClearCompany,
  } = props

  const [selected, setSelected] = useState(company)
  const [isChecked, setIsChecked] = useState(isToggleChecked)
  const [isActiveSearch, setIsActiveSearch] = useState(false)

  const { data, companies, hasMore, loading, error } = useDoubleTheDonationCompaniesSearch(
    query,
    pageNumber,
  )

  const onSelect = ({ value, label }: ScrollableListData) => {
    setSelected(label)

    const company = companies.find((company) => company.id === Number(value))

    if (company) {
      onSelectCompany({
        companyId: value,
        companyName: company.company_name,
      })
    }
  }

  const OnSearchActive = (value: boolean) => {
    setIsActiveSearch(value)
  }

  const handleChange = () => {
    if (isChecked) {
      onClearCompany()
    }

    setIsChecked(!isChecked)
    setSelected('')
    setIsActiveSearch(false)
    if (onChange) onChange()
  }

  const onLinkClick = () => {
    setSelected('')

    onClearCompany()
  }

  const onSelectKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      setSelected('')

      onClearCompany()
    }
  }

  const fetchNewData = (newQuery: string, newPageNumber: number) => {
    setQuery(newQuery)
    setPageNumber(newPageNumber)
  }

  return (
    <DoubleDonationContainer>
      <ListItem
        style={{ paddingTop: '44px' }}
        label={t('orderDoubleTheDonationSelector.employerMatching.label', 'Employer Matching')}
        disabled={false}
        icon={icon}
        subtitle=""
        onChange={handleChange}
        onClick={handleChange}
        showToggleSwitch={true}
        checked={isChecked}
      >
        {selected ? (
          <>
            <Label size="default" color={DEFAULT_THEME_COLORS['--theme-color--neutral-10']}>
              {t('orderDoubleTheDonationSelector.selectedCompany.label', 'Company: {{selected}}', {
                selected,
              })}
            </Label>
            <StyledButton
              tabIndex={0}
              role="button"
              onClick={onLinkClick}
              onKeyDown={(e) => onSelectKeyDown(e)}
              data-id="double-the-donation-drawer-choose-another-button"
            >
              {t(
                'orderDoubleTheDonationSelector.selectDifferentCompany.label',
                'Select a different company',
              )}
            </StyledButton>
          </>
        ) : (
          <ScrollableList
            data={data}
            selected={selected}
            isActivatedSearch={true}
            hasMore={hasMore}
            loading={loading}
            error={error}
            height={300}
            isPopUpResult={true}
            fetchNewData={fetchNewData}
            onSelect={onSelect}
            OnSearchActive={OnSearchActive}
            dataId="double-the-donation-drawer"
          />
        )}

        {(!isActiveSearch || selected) && (
          <Caption style={{ paddingTop: '5px' }}>
            {t(
              'orderDoubleTheDonationSelector.donationCaption',
              'Check to see if your employer will match your donation. You may receive an email on how to submit your match request to maximize your impact.',
            )}
          </Caption>
        )}
      </ListItem>
    </DoubleDonationContainer>
  )
}

export default DoubleDonation
